import { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Shield, CheckCircle, AlertTriangle } from 'lucide-react';

interface RecaptchaV3Props {
  onValidation: (isValid: boolean, token?: string) => void;
  action?: string;
}

export function RecaptchaV3({ onValidation, action = 'signup' }: RecaptchaV3Props) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [status, setStatus] = useState<'idle' | 'verifying' | 'verified' | 'failed'>('idle');

  useEffect(() => {
    const handleReCaptcha = async () => {
      if (!executeRecaptcha) {
        console.log('Execute recaptcha not yet available');
        return;
      }

      setStatus('verifying');

      try {
        const token = await executeRecaptcha(action);
        
        if (token) {
          setStatus('verified');
          onValidation(true, token);
        } else {
          setStatus('failed');
          onValidation(false);
        }
      } catch (error) {
        console.error('reCAPTCHA error:', error);
        setStatus('failed');
        onValidation(false);
      }
    };

    // Auto-execute reCAPTCHA when component mounts
    const timer = setTimeout(handleReCaptcha, 1000);
    return () => clearTimeout(timer);
  }, [executeRecaptcha, action, onValidation]);

  return (
    <div className={`flex items-start gap-2 mt-2 text-xs ${
      status === 'verified' ? 'text-green-600' :
      status === 'failed' ? 'text-red-600' :
      'text-muted-foreground'
    }`}>
      {status === 'verifying' && <Shield className="h-4 w-4 animate-pulse" />}
      {status === 'verified' && <CheckCircle className="h-4 w-4" />}
      {status === 'failed' && <AlertTriangle className="h-4 w-4" />}
      <span>
        {status === 'idle' && 'Initializing security check...'}
        {status === 'verifying' && 'Verifying you are human...'}
        {status === 'verified' && 'Security verification complete'}
        {status === 'failed' && 'Security verification failed. Please refresh and try again.'}
      </span>
    </div>
  );
}
